import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar, 
  Plus, 
  CheckCircle2, 
  Circle, 
  ArrowLeft,
  Clock,
  User,
  MessageSquare,
  Trash2,
  Edit,
  MoreVertical,
  AlertCircle,
  Check,
  Send,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const TaskEventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState({ name: '', description: '', date: '' });
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '', 
    assignees: [], 
    due_date: '' 
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Task completion dialog state
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [completePhoto, setCompletePhoto] = useState({ url: null, preview: null });
  const [completeComment, setCompleteComment] = useState('');
  const [submittingComplete, setSubmittingComplete] = useState(false);
  const completeFileInputRef = useRef(null);
  
  const canCreateTasks = true; // Všichni uživatelé mají přístup

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, usersRes] = await Promise.all([
        api.get(`/task-events/${eventId}`),
        api.get('/users/list')
      ]);
      setEvent(eventRes.data);
      setUsers(usersRes.data.filter(u => u.approved));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
      navigate('/ukoly');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Zadejte název úkolu');
      return;
    }
    if (!newTask.due_date) {
      toast.error('Zadejte termín splnění');
      return;
    }
    if (newTask.assignees.length === 0) {
      toast.error('Vyberte alespoň jednoho řešitele');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/tasks', {
        event_id: eventId,
        ...newTask
      });
      await fetchData();
      setShowNewTaskDialog(false);
      setNewTask({ title: '', description: '', assignees: [], due_date: '' });
      toast.success('Úkol vytvořen');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Chyba při vytváření úkolu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async () => {
    try {
      setSubmitting(true);
      await api.put(`/task-events/${eventId}`, editingEvent);
      await fetchData();
      setShowEditEventDialog(false);
      toast.success('Událost aktualizována');
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Chyba při aktualizaci události');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Opravdu chcete smazat tuto událost včetně všech úkolů?')) return;
    
    try {
      await api.delete(`/task-events/${eventId}`);
      toast.success('Událost smazána');
      navigate('/ukoly');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Chyba při mazání události');
    }
  };

  const handleToggleTask = async (task) => {
    if (!task.completed) {
      // Opening dialog for photo/comment when completing
      setCompletingTask(task);
      setCompletePhoto({ url: null, preview: null });
      setCompleteComment('');
      setShowCompleteDialog(true);
    } else {
      // Uncompleting - no photo needed
      try {
        await api.put(`/tasks/${task.id}`, { completed: false });
        await fetchData();
        if (selectedTask?.id === task.id) {
          const updatedTask = await api.get(`/tasks/${task.id}`);
          setSelectedTask(updatedTask.data);
        }
        toast.success('Úkol označen jako nesplněný');
      } catch (error) {
        console.error('Error toggling task:', error);
        toast.error('Chyba při aktualizaci úkolu');
      }
    }
  };

  // Get full image URL - handles both base64 (legacy) and server URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/')) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${backendUrl}${url}`;
    }
    return url;
  };

  const handleCompletePhotoUpload = async (file) => {
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setCompletePhoto(prev => ({ ...prev, preview: e.target.result }));
    };
    reader.readAsDataURL(file);
    
    // Upload to server
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompletePhoto(prev => ({ ...prev, url: response.data.url }));
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Chyba při nahrávání fotografie');
    }
  };

  const handleSubmitComplete = async () => {
    if (!completePhoto.url && !completeComment.trim()) {
      toast.error('Nahrajte fotografii nebo zadejte komentář');
      return;
    }
    
    try {
      setSubmittingComplete(true);
      await api.put(`/tasks/${completingTask.id}`, { 
        completed: true,
        completed_photo: completePhoto.url || null,
        completed_comment: completeComment.trim() || null
      });
      await fetchData();
      if (selectedTask?.id === completingTask.id) {
        const updatedTask = await api.get(`/tasks/${completingTask.id}`);
        setSelectedTask(updatedTask.data);
      }
      setShowCompleteDialog(false);
      setCompletingTask(null);
      setCompletePhoto({ url: null, preview: null });
      setCompleteComment('');
      toast.success('Úkol splněn');
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(error.response?.data?.detail || 'Chyba při splnění úkolu');
    } finally {
      setSubmittingComplete(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Opravdu chcete smazat tento úkol?')) return;
    
    try {
      await api.delete(`/tasks/${taskId}`);
      await fetchData();
      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
      }
      toast.success('Úkol smazán');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Chyba při mazání úkolu');
    }
  };

  const handleSelectTask = async (task) => {
    try {
      const response = await api.get(`/tasks/${task.id}`);
      setSelectedTask(response.data);
    } catch (error) {
      console.error('Error fetching task details:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    
    try {
      await api.post(`/tasks/${selectedTask.id}/comments`, { content: newComment });
      const response = await api.get(`/tasks/${selectedTask.id}`);
      setSelectedTask(response.data);
      setNewComment('');
      await fetchData();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Chyba při přidávání komentáře');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateStr;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const isOverdue = (task) => {
    if (task.completed) return false;
    const today = new Date().toISOString().split('T')[0];
    return task.due_date < today;
  };

  const isMyTask = (task) => {
    return task.assignees?.includes(user?.id);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Událost nenalezena</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/ukoly')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zpět na seznam
          </Button>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{event.name}</h1>
              <div className="flex items-center gap-3 mt-2 text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(event.date)}
                </span>
                <span>•</span>
                <span>{event.tasks?.length || 0} úkolů</span>
              </div>
              {event.description && (
                <p className="text-slate-600 mt-2">{event.description}</p>
              )}
            </div>
            
            {canCreateTasks && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowNewTaskDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="new-task-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Přidat úkol
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => {
                      setEditingEvent({
                        name: event.name,
                        description: event.description || '',
                        date: event.date
                      });
                      setShowEditEventDialog(true);
                    }}>
                      <Edit className="w-4 h-4 mr-2" />
                      Upravit událost
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={handleDeleteEvent}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Smazat událost
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tasks List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-slate-800">Úkoly</h2>
              </div>
              
              {!event.tasks || event.tasks.length === 0 ? (
                <div className="p-8 text-center">
                  <Circle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Zatím žádné úkoly</p>
                  {canCreateTasks && (
                    <Button 
                      onClick={() => setShowNewTaskDialog(true)}
                      className="mt-4 bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Přidat první úkol
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {event.tasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => handleSelectTask(task)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedTask?.id === task.id ? 'bg-blue-50' : ''
                      } ${isOverdue(task) ? 'border-l-4 border-l-red-500' : ''}`}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTask(task);
                          }}
                          className={`mt-0.5 rounded-full p-1 transition-colors ${
                            task.completed 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-slate-400 hover:bg-slate-100'
                          }`}
                          data-testid={`toggle-task-${task.id}`}
                        >
                          {task.completed ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <Circle className="w-5 h-5" />
                          )}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium ${task.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {task.title}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                            <span className={`flex items-center gap-1 ${
                              isOverdue(task) ? 'text-red-600' : 'text-slate-500'
                            }`}>
                              <Clock className="w-3.5 h-3.5" />
                              {formatDate(task.due_date)}
                              {isOverdue(task) && (
                                <AlertCircle className="w-3.5 h-3.5" />
                              )}
                            </span>
                            
                            {task.assignee_names?.length > 0 && (
                              <span className="flex items-center gap-1 text-slate-500">
                                <User className="w-3.5 h-3.5" />
                                {task.assignee_names.join(', ')}
                              </span>
                            )}
                            
                            {task.comment_count > 0 && (
                              <span className="flex items-center gap-1 text-slate-500">
                                <MessageSquare className="w-3.5 h-3.5" />
                                {task.comment_count}
                              </span>
                            )}
                            
                            {isMyTask(task) && !task.completed && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                                Můj úkol
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {canCreateTasks && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTask(task.id);
                            }}
                            className="text-slate-400 hover:text-red-500 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Task Detail Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border sticky top-6">
              {selectedTask ? (
                <>
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-slate-800">Detail úkolu</h3>
                  </div>
                  <div className="p-4">
                    <h4 className={`font-medium text-lg mb-3 ${selectedTask.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      {selectedTask.title}
                    </h4>
                    
                    {selectedTask.description && (
                      <p className="text-slate-600 text-sm mb-4">{selectedTask.description}</p>
                    )}
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Termín:</span>
                        <span className={isOverdue(selectedTask) ? 'text-red-600 font-medium' : 'text-slate-800'}>
                          {formatDate(selectedTask.due_date)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-slate-500">Stav:</span>
                        <span className={selectedTask.completed ? 'text-green-600' : 'text-orange-500'}>
                          {selectedTask.completed ? 'Splněno' : 'Nesplněno'}
                        </span>
                      </div>
                      
                      {selectedTask.completed && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Splnil:</span>
                          <span className="text-slate-800">{selectedTask.completed_by_name}</span>
                        </div>
                      )}
                      
                      {selectedTask.completed && selectedTask.completed_photo && (
                        <div>
                          <span className="text-slate-500">Foto splnění:</span>
                          <img 
                            src={getImageUrl(selectedTask.completed_photo)} 
                            alt="Foto splnění"
                            className="w-full h-32 object-cover rounded mt-2 cursor-pointer hover:opacity-80"
                            onClick={() => window.open(getImageUrl(selectedTask.completed_photo), '_blank')}
                          />
                        </div>
                      )}
                      
                      <div>
                        <span className="text-slate-500">Řešitelé:</span>
                        <div className="mt-1">
                          {selectedTask.assignee_names?.map((name, idx) => (
                            <span key={idx} className="inline-block px-2 py-1 bg-slate-100 rounded text-slate-700 text-xs mr-1 mb-1">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-slate-500">Zadal:</span>
                        <span className="text-slate-800">{selectedTask.created_by_name}</span>
                      </div>
                    </div>
                    
                    {/* Comments */}
                    <div className="mt-6 pt-4 border-t">
                      <h5 className="font-medium text-slate-800 mb-3">
                        Komentáře ({selectedTask.comments?.length || 0})
                      </h5>
                      
                      <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                        {selectedTask.comments?.map((comment) => (
                          <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-sm font-medium text-slate-700">{comment.user_name}</span>
                              <span className="text-xs text-slate-400">{formatDateTime(comment.created_at)}</span>
                            </div>
                            <p className="text-sm text-slate-600">{comment.content}</p>
                          </div>
                        ))}
                        {(!selectedTask.comments || selectedTask.comments.length === 0) && (
                          <p className="text-sm text-slate-400 text-center py-2">Zatím žádné komentáře</p>
                        )}
                      </div>
                      
                      {/* Add comment */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Napište komentář..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddComment();
                            }
                          }}
                          data-testid="comment-input"
                        />
                        <Button 
                          size="icon"
                          onClick={handleAddComment}
                          disabled={!newComment.trim()}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Vyberte úkol pro zobrazení detailu</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* New Task Dialog */}
        <Dialog open={showNewTaskDialog} onOpenChange={setShowNewTaskDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nový úkol</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="task-title">Název úkolu *</Label>
                <Input
                  id="task-title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Co je potřeba udělat?"
                  data-testid="task-title-input"
                />
              </div>
              <div>
                <Label htmlFor="task-due-date">Termín splnění *</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  data-testid="task-due-date-input"
                />
              </div>
              <div>
                <Label>Řešitelé *</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={newTask.assignees.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewTask({ ...newTask, assignees: [...newTask.assignees, u.id] });
                          } else {
                            setNewTask({ ...newTask, assignees: newTask.assignees.filter(id => id !== u.id) });
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="task-description">Popis</Label>
                <Textarea
                  id="task-description"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Volitelný popis úkolu..."
                  rows={3}
                  data-testid="task-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewTaskDialog(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleCreateTask} 
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-task-submit"
              >
                {submitting ? 'Vytváření...' : 'Vytvořit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={showEditEventDialog} onOpenChange={setShowEditEventDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upravit událost</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-event-name">Název události *</Label>
                <Input
                  id="edit-event-name"
                  value={editingEvent.name}
                  onChange={(e) => setEditingEvent({ ...editingEvent, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-event-date">Datum události *</Label>
                <Input
                  id="edit-event-date"
                  type="date"
                  value={editingEvent.date}
                  onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-event-description">Popis</Label>
                <Textarea
                  id="edit-event-description"
                  value={editingEvent.description}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditEventDialog(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleUpdateEvent} 
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? 'Ukládání...' : 'Uložit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Task Completion Dialog */}
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Splnění úkolu</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Pro splnění úkolu je nutné nahrát fotografii <strong>nebo</strong> zadat komentář.
              </p>
              
              {completingTask && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium text-slate-800">{completingTask.title}</p>
                </div>
              )}
              
              <div>
                <Label>Fotografie splnění</Label>
                <div className="mt-2">
                  {completePhoto.preview ? (
                    <div className="relative">
                      <img 
                        src={completePhoto.preview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setCompletePhoto({ url: null, preview: null })}
                      >
                        Odstranit
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => completeFileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-400 transition-colors"
                    >
                      <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Klikněte pro nahrání fotografie</p>
                    </div>
                  )}
                  <input
                    ref={completeFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCompletePhotoUpload(file);
                    }}
                  />
                </div>
              </div>

              <div className="text-center text-sm text-slate-400">nebo</div>

              <div>
                <Label htmlFor="complete-comment">Komentář</Label>
                <Textarea
                  id="complete-comment"
                  value={completeComment}
                  onChange={(e) => setCompleteComment(e.target.value)}
                  placeholder="Popis splnění / poznámka..."
                  rows={3}
                  className="mt-2"
                  data-testid="complete-comment"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCompleteDialog(false);
                setCompletingTask(null);
                setCompletePhoto({ url: null, preview: null });
                setCompleteComment('');
              }}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSubmitComplete}
                disabled={submittingComplete || (!completePhoto.url && !completeComment.trim())}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-complete-task"
              >
                {submittingComplete ? 'Ukládám...' : 'Splnit úkol'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TaskEventDetailPage;
